import { PROTOCOL_ERROR_CODE } from '@/core/types/error-codes';
import { API_BASE, apiErrorFromResponse } from './client';
import { authedFetch } from './refresh';
import type { AiChatRequest } from '@/generated/ts-rs/AiChatRequest';
import type { AiChatStreamDoneData } from '@/generated/ts-rs/AiChatStreamDoneData';
import type { AiChatStreamErrorData } from '@/generated/ts-rs/AiChatStreamErrorData';
import type { AiChatStreamPendingData } from '@/generated/ts-rs/AiChatStreamPendingData';
import type { AiChatStreamToolData } from '@/generated/ts-rs/AiChatStreamToolData';

const NETWORK_ERROR_CODE = 9003;

export type { AiChatRequest };
export type StreamDoneData = AiChatStreamDoneData;
export type StreamToolData = AiChatStreamToolData;
export type StreamPendingData = AiChatStreamPendingData;
export type StreamErrorData = AiChatStreamErrorData;

export interface StreamCallbacks {
  onDelta: (text: string) => void;
  onTool: (data: StreamToolData) => void;
  onDone: (data: StreamDoneData) => void;
  /** Turn suspended on a gated mutation; render a confirmation card + Resolve. */
  onPending: (data: StreamPendingData) => void;
  onError: (data: StreamErrorData) => void;
}

/**
 * Stream AI chat via SSE.
 * Returns an AbortController so the caller can cancel the stream.
 */
export function streamAiChat(
  token: string,
  body: AiChatRequest,
  callbacks: StreamCallbacks,
): AbortController {
  const controller = new AbortController();

  (async () => {
    try {
      const res = await authedFetch(token, (tk) =>
        fetch(`${API_BASE}/api/tenant/ai/chat/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tk}`,
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        }),
      );

      if (!res.ok) {
        const error = await apiErrorFromResponse(res);
        callbacks.onError({ code: error.code, message: error.message });
        return;
      }

      const reader = res.body?.getReader();
      if (!reader) {
        callbacks.onError(protocolError('AI stream response body is missing'));
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let terminalEventSeen = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse SSE lines
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? ''; // Keep incomplete line in buffer

        let currentEvent = '';
        for (const rawLine of lines) {
          const line = rawLine.replace(/\r$/, ''); // Strip CRLF
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            const data = line.slice(6);
            terminalEventSeen = handleSseEvent(currentEvent, data, callbacks);
            if (terminalEventSeen) return;
            currentEvent = '';
          }
          // Empty line = end of event (SSE spec), reset
          if (line === '') {
            currentEvent = '';
          }
        }
      }

      if (!terminalEventSeen) {
        callbacks.onError(protocolError('AI stream ended without done or error event'));
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        callbacks.onError({
          code: NETWORK_ERROR_CODE,
          message: 'AI stream network error',
        });
      }
    }
  })();

  return controller;
}

type JsonObjectResult =
  | { kind: 'ok'; value: Record<string, unknown> }
  | { kind: 'err'; error: StreamErrorData };

function protocolError(message: string): StreamErrorData {
  return { code: PROTOCOL_ERROR_CODE, message };
}

function parseJsonObject(event: string, data: string): JsonObjectResult {
  try {
    const parsed = JSON.parse(data) as unknown;
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return { kind: 'ok', value: parsed as Record<string, unknown> };
    }
  } catch {
    // Fall through to the protocol error below.
  }
  return { kind: 'err', error: protocolError(`AI stream ${event} event is not a JSON object`) };
}

function handleSseEvent(event: string, data: string, callbacks: StreamCallbacks): boolean {
  switch (event) {
    case 'delta': {
      // data is JSON-encoded string: "text chunk"
      try {
        const text = JSON.parse(data) as unknown;
        if (typeof text !== 'string') {
          callbacks.onError(protocolError('AI stream delta event is not a JSON string'));
          return true;
        }
        callbacks.onDelta(text);
      } catch {
        callbacks.onError(protocolError('AI stream delta event is not valid JSON'));
        return true;
      }
      break;
    }
    case 'tool': {
      const parsed = parseJsonObject('tool', data);
      if (parsed.kind === 'err') {
        callbacks.onError(parsed.error);
        return true;
      }
      const name = parsed.value.name;
      if (typeof name !== 'string' || name.length === 0) {
        callbacks.onError(protocolError('AI stream tool event missing name'));
        return true;
      }
      callbacks.onTool({ name });
      break;
    }
    case 'done': {
      const parsed = parseJsonObject('done', data);
      if (parsed.kind === 'err') {
        callbacks.onError(parsed.error);
        return true;
      }
      const sessionId = parsed.value.session_id;
      const usedTokens = parsed.value.used_tokens;
      const dailyLimit = parsed.value.daily_limit;
      if (
        typeof sessionId !== 'string'
        || typeof usedTokens !== 'number'
        || typeof dailyLimit !== 'number'
      ) {
        callbacks.onError(protocolError('AI stream done event missing usage facts'));
        return true;
      }
      callbacks.onDone({
        session_id: sessionId,
        used_tokens: usedTokens,
        daily_limit: dailyLimit,
      });
      return true;
    }
    case 'pending': {
      const parsed = parseJsonObject('pending', data);
      if (parsed.kind === 'err') {
        callbacks.onError(parsed.error);
        return true;
      }
      const sessionId = parsed.value.session_id;
      const actionId = parsed.value.action_id;
      const toolName = parsed.value.tool_name;
      const description = parsed.value.description;
      const proposedInput = parsed.value.proposed_input;
      if (
        typeof sessionId !== 'string'
        || typeof actionId !== 'string'
        || typeof toolName !== 'string'
        || typeof description !== 'string'
        || typeof proposedInput !== 'object'
        || proposedInput === null
        || Array.isArray(proposedInput)
      ) {
        callbacks.onError(protocolError('AI stream pending event missing action facts'));
        return true;
      }
      callbacks.onPending({
        session_id: sessionId,
        action_id: actionId,
        tool_name: toolName,
        description,
        proposed_input: proposedInput as Record<string, unknown>,
      });
      return true;
    }
    case 'error': {
      const parsed = parseJsonObject('error', data);
      if (parsed.kind === 'err') {
        callbacks.onError(parsed.error);
        return true;
      }
      const code = parsed.value.code;
      const message = parsed.value.message;
      if (typeof code !== 'number' || typeof message !== 'string') {
        callbacks.onError(protocolError('AI stream error event missing numeric code or message'));
        return true;
      }
      callbacks.onError({ code, message });
      return true;
    }
  }
  return false;
}
